/**
 * Database Block Transformer
 *
 * Registers attachment/image cell blob IDs in the pathBlobIdMap
 * so they are included in snapshot exports and restored during imports.
 */

import type {
  BlockSnapshotLeaf,
  FromSnapshotPayload,
  SnapshotNode,
  ToSnapshotPayload,
} from '@blocksuite/store';
import { BaseBlockTransformer } from '@blocksuite/store';
import { createLegacyInlineDatabaseIdentityProps } from '@streamlain/database-identity';

import type { DatabaseBlockProps } from './database-model.js';
import type { CellDataType, ColumnDataType, SerializedCells } from './types.js';

/**
 * Extracts blob IDs from database cell values for attachment/image columns.
 */
function collectCellBlobIds(
  cells: SerializedCells,
  columns: ColumnDataType[]
): string[] {
  const attachmentColIds = new Set<string>();
  const imageColIds = new Set<string>();

  for (const col of columns) {
    if (col.type === 'attachment') attachmentColIds.add(col.id);
    else if (col.type === 'image') imageColIds.add(col.id);
  }

  if (attachmentColIds.size === 0 && imageColIds.size === 0) return [];

  const blobIds: string[] = [];

  for (const rowCells of Object.values(cells)) {
    for (const colId of attachmentColIds) {
      const cell = rowCells[colId] as CellDataType | undefined;
      if (!cell?.value || typeof cell.value !== 'object') continue;
      // Attachment value: Record<blobId, {id, name, mime, order}>
      for (const blobId of Object.keys(cell.value as Record<string, unknown>)) {
        if (blobId) blobIds.push(blobId);
      }
    }

    for (const colId of imageColIds) {
      const cell = rowCells[colId] as CellDataType | undefined;
      // Image value: string | null
      if (typeof cell?.value === 'string' && cell.value) {
        blobIds.push(cell.value);
      }
    }
  }

  return blobIds;
}

export class DatabaseBlockTransformer extends BaseBlockTransformer<DatabaseBlockProps> {
  override toSnapshot(
    snapshot: ToSnapshotPayload<DatabaseBlockProps>
  ): BlockSnapshotLeaf {
    const snapshotRet = super.toSnapshot(snapshot);

    const cells = snapshot.model.props.cells as SerializedCells;
    const columns = snapshot.model.props.columns as ColumnDataType[];
    if (!cells || !columns) return snapshotRet;

    const blobIds = collectCellBlobIds(cells, columns);
    if (blobIds.length === 0) return snapshotRet;

    const pathBlobIdMap = snapshot.assets.getPathBlobIdMap();
    const blockId = snapshot.model.id;
    for (const blobId of blobIds) {
      // Use composite key to avoid collisions (one DB can have many blobs)
      pathBlobIdMap.set(`${blockId}:cell:${blobId}`, blobId);
    }

    return snapshotRet;
  }

  override async fromSnapshot(
    payload: FromSnapshotPayload
  ): Promise<SnapshotNode<DatabaseBlockProps>> {
    const snapshotRet = await super.fromSnapshot(payload);

    if (
      snapshotRet.props.databaseId === undefined &&
      snapshotRet.props.databaseStorageVersion === undefined &&
      snapshotRet.props.databaseRootDocId === undefined &&
      snapshotRet.props.databaseShardManifest === undefined
    ) {
      Object.assign(
        snapshotRet.props,
        createLegacyInlineDatabaseIdentityProps(snapshotRet.id)
      );
    }

    if (payload.assets.isEmpty()) return snapshotRet;

    const cells = snapshotRet.props.cells as SerializedCells;
    const columns = snapshotRet.props.columns as ColumnDataType[];
    if (!cells || !columns) return snapshotRet;

    const blobIds = collectCellBlobIds(cells, columns);
    const assetsMap = payload.assets.getAssets();

    for (const blobId of blobIds) {
      // During export, blobs are stored with composite key "blockId:cell:blobId".
      // The ZIP import loads them into assetsMap under that composite key.
      // We need to find the blob by composite key, re-register under the real blobId,
      // then write it to blob storage.
      if (!assetsMap.has(blobId)) {
        // Find by composite key suffix
        for (const [key, blob] of assetsMap) {
          if (key.endsWith(`:cell:${blobId}`)) {
            assetsMap.set(blobId, blob);
            break;
          }
        }
      }
      if (assetsMap.has(blobId)) {
        await payload.assets.writeToBlob(blobId);
      }
    }

    return snapshotRet;
  }
}
